import * as React from 'react';
import { ContentState } from 'draft-js';
import { PencilIcon, TrashIcon } from '@revfluence/fresh-icons/regular/esm';
import { Button } from '@/shadcn/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shadcn/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shadcn/components/ui/tooltip';
import { TableEntityData } from '../StyleControls/types';

interface IProps {
  contentState: ContentState;
  entityKey: string;
  onEditTable?: (tableData: TableEntityData, entityKey: string) => void;
  onDeleteTable?: (entityKey: string) => void;
}

export const TableBlock: React.FC<IProps> = ({
  contentState,
  entityKey,
  onEditTable,
  onDeleteTable,
}) => {
  const entity = contentState.getEntity(entityKey);
  const data = entity.getData() as { data: TableEntityData };
  const tableData = data.data;

  const handleEditClick = () => {
    if (onEditTable) {
      onEditTable(tableData, entityKey);
    }
  };

  const handleDeleteClick = () => {
    if (onDeleteTable && window.confirm('Are you sure you want to delete this table?')) {
      onDeleteTable(entityKey);
    }
  };

  const getTableAlignment = () => {
    switch (tableData.alignment) {
      case 'center': return 'mx-auto';
      case 'right': return 'ml-auto';
      default: return 'mr-auto';
    }
  };

  const getBorderStyle = () => {
    switch (tableData.borderStyle) {
      case 'solid': return 'border border-gray-300';
      case 'dashed': return 'border border-dashed border-gray-300';
      default: return '';
    }
  };

  return (
    <div className="my-4 relative group">
      {/* Edit and Delete Buttons */}
      <div className="flex items-center gap-2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={handleEditClick}
                className="h-8 px-3"
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                Edit Table
              </Button>
            </TooltipTrigger>
            <TooltipContent>Edit table</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeleteClick}
                className="h-8 px-3 text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </TooltipTrigger>
            <TooltipContent>Delete table</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Read-only Table Display */}
      <div className={`w-full ${getTableAlignment()}`}>
        <Table className={`${getBorderStyle()} ${tableData.width || 'w-full'}`}>
          {tableData.headerRow && (
            <TableHeader>
              <TableRow>
                {tableData.rows[0]?.map((cell, colIndex) => (
                  <TableHead key={colIndex}>
                    {cell.content || `Header ${colIndex + 1}`}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
          )}

          <TableBody>
            {tableData.rows.slice(tableData.headerRow ? 1 : 0).map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {row.map((cell, colIndex) => (
                  <TableCell key={colIndex}>
                    {cell.content || `Cell ${rowIndex + 1}-${colIndex + 1}`}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

TableBlock.displayName = 'TableBlock';
